// What can be done to an open order from more than one screen (the order's own
// screen and the Orders tab's list): print the guest check, send to the kitchen.
import { kotLines, orderLines, sendToKitchen, setOrderBilled, type SyncOp } from '@ims/core'
import type {
  InvoiceDetail,
  InvoiceRow,
  OrderDetail,
  Repository,
  TenantSettingsRow,
} from '@ims/data'
import { MenuItem, SplitButton, type useToast } from '@ims/ui'
import { ChefHat, Check } from '@ims/ui/icons'
import { useState, type ReactNode } from 'react'
import { printsOnSend, readSendPrint, saveSendPrint, type SendPrint } from '../lib/kot-send.ts'
import { displayName } from '../lib/account.ts'
import { billAdjustments, type BillAdjustments } from '../lib/bill-adjustments.ts'
import type { PrintedBy } from '../lib/doc-view.ts'
import { useSession } from '../session.tsx'
import { nextPrint } from '../lib/print-count.ts'
import { invoicePrintOp } from '../lib/rows.ts'
import { todayIso } from '../lib/format.ts'
import {
  STATION_LABEL,
  clock,
  ticketName,
  orderItemRows,
  orderRow,
  orderServiceFor,
  startOfToday,
  type BillService,
} from '../lib/orders.ts'
import {
  GuestCheck,
  InvoiceA4,
  KotSlips,
  Receipt,
  type BatchLabels,
  type BillPrintMeta,
  printDocument,
  printName,
  type KotSlipData,
  type PrintFormat,
} from '../print.tsx'

export interface OrderActionDeps {
  repo: Repository
  settings: TenantSettingsRow | null | undefined
  toast: ReturnType<typeof useToast>
  context: () => { tenantId: string; now: string }
  write: (ops: () => SyncOp[]) => Promise<boolean>
}

/**
 * Sends what is new to the kitchen: a ticket per station, a toast, and the slips when they
 * print: `print` when the caller chose (the arrow beside the button), else this device's
 * choice, else the business setting; `forcePrint` (offline on Counter) always prints.
 */
export async function sendOrderToKitchen(
  { repo, settings, toast, context, write }: OrderActionDeps,
  detail: OrderDetail,
  where: string | null,
  options: { forcePrint?: boolean; print?: boolean } = {},
): Promise<void> {
  const last = await repo.orders.lastKotNo(startOfToday())
  const built = sendToKitchen(
    context(),
    orderRow(detail.order),
    orderItemRows(detail.items),
    detail.sent,
    last + 1,
  )
  if (built.tickets.length === 0) return
  if (!(await write(() => built.ops))) return
  const prints = options.forcePrint || (options.print ?? printsOnSend(readSendPrint(), settings))
  // "Sent KOT 12 to the kitchen and BOT 13 to the bar · printing"
  toast.success(
    `Sent ${built.tickets
      .map((t) => `${ticketName(t.ticket)} to the ${STATION_LABEL[t.ticket.station].toLowerCase()}`)
      .join(' and ')}${prints ? ' · printing' : ''}`,
  )
  if (prints) {
    printDocument(
      <KotSlips
        slips={built.tickets.map((t): KotSlipData => ({
          kotNo: t.ticket.kot_no,
          name: ticketName(t.ticket),
          station: STATION_LABEL[t.ticket.station],
          tableName: where,
          time: clock(t.ticket.sent_at),
          lines: kotLines(t.rows),
        }))}
      />,
      'receipt',
    )
  }
}

/**
 * Send to kitchen (canvas row `kitchen-tickets`): the main tap sends as this device or the
 * business set it; the arrow offers "Send and print" or "Send only, no paper", which is kept
 * on this device and used for this send. `onSend(print)` gets the choice for this send.
 */
export function SendToKitchenButton({
  label,
  onSend,
  loading,
  disabled,
  className,
  settings,
}: {
  label: ReactNode
  onSend: (print: boolean) => void
  loading?: boolean
  disabled?: boolean
  className?: string
  settings: TenantSettingsRow | null | undefined
}) {
  const [choice, setChoice] = useState<SendPrint | null>(readSendPrint)
  const prints = printsOnSend(choice, settings)
  const pick = (next: SendPrint) => {
    saveSendPrint(next)
    setChoice(next)
    onSend(next === 'print')
  }
  return (
    <SplitButton
      label={label}
      icon={<ChefHat className="size-5" />}
      onClick={() => onSend(prints)}
      loading={loading}
      disabled={disabled}
      className={className}
      menuLabel="How to send"
      menuWidth={300}
    >
      <MenuItem
        checked={prints}
        icon={prints ? <Check className="size-4" /> : <span className="size-4" />}
        onSelect={() => pick('print')}
      >
        <SendChoice title="Send and print" line="Tickets print at the kitchen and bar printer." />
      </MenuItem>
      <MenuItem
        checked={!prints}
        icon={!prints ? <Check className="size-4" /> : <span className="size-4" />}
        onSelect={() => pick('paper-free')}
      >
        <SendChoice title="Send only, no paper" line="The kitchen reads them on its screen." />
      </MenuItem>
      <p className="border-t border-border px-3 pt-2 pb-1 text-xs text-muted">
        This device remembers your choice. The business default is in Settings → Tables.
      </p>
    </SplitButton>
  )
}

function SendChoice({ title, line }: { title: string; line: string }) {
  return (
    <span className="block">
      <span className="block font-semibold">{title}</span>
      <span className="block text-xs text-muted">{line}</span>
    </span>
  )
}

/** Prints the guest check; the order then waits to be paid ("bill printed"). */
export async function printGuestCheck(
  { settings, context, write }: OrderActionDeps,
  detail: OrderDetail,
  where: string | null,
): Promise<void> {
  const order = orderRow(detail.order)
  if (order.status !== 'billed' && !(await write(() => setOrderBilled(context(), order, true).ops)))
    return
  printDocument(
    <GuestCheck
      lines={orderLines(orderItemRows(detail.items))}
      tableName={where}
      covers={order.covers}
      date={todayIso()}
      time={clock(new Date().toISOString())}
      settings={settings}
    />,
    'receipt',
  )
}

/**
 * Every print of a bill goes through here (docs/plans/print-copies.md): the first print of
 * an issued bill is the original, each later one, in either format and from any device, is
 * "COPY OF ORIGINAL - N". The paper comes first; recording the print is its own tx after,
 * and a failure there goes to `onRecordError`, never stopping the printer. Drafts are
 * neither counted nor marked.
 */
export async function printBill(
  repo: Repository,
  tenantId: string,
  invoice: InvoiceRow,
  format: PrintFormat,
  render: (copyLabel: string | null) => ReactNode,
  onRecordError?: (error: unknown) => void,
): Promise<void> {
  const issued = invoice.status !== 'draft'
  const prints = issued
    ? (await repo.list('invoice_prints', { where: { invoice_id: invoice.id }, limit: 1000 })).rows
    : []
  const next = issued ? nextPrint(invoice, prints) : { copyNo: 0, label: null }
  printDocument(
    render(next.label),
    format,
    printName(format === 'a4' ? 'Invoice' : 'Receipt', invoice),
  )
  if (!issued) return
  try {
    await repo.commit([
      invoicePrintOp({ tenantId }, { invoice_id: invoice.id, format, copy_no: next.copyNo }),
    ])
  } catch (error) {
    onRecordError?.(error)
  }
}

/** Everything a bill's print or PDF needs, read now (the invoice screen keeps its own copy). */
export interface BillPrintData {
  detail: InvoiceDetail
  service: BillService | null
  adjustments: BillAdjustments | null
  batches: BatchLabels
}

export async function loadBillPrint(
  repo: Repository,
  invoiceId: string,
  serverName: (id: string | null) => string | null,
): Promise<BillPrintData | null> {
  const detail = await repo.invoices.detail(invoiceId)
  if (!detail) return null
  const service = await orderServiceFor(repo, detail.invoice, serverName)
  const credits = (await repo.list('credit_notes', { where: { invoice_id: invoiceId }, limit: 50 }))
    .rows
  const batches: BatchLabels = {}
  for (const id of new Set(detail.lines.map((l) => l.batch_id))) {
    const batch = id ? await repo.get('batches', id) : null
    if (batch) batches[batch.id] = { batch_no: batch.batch_no, expiry_date: batch.expiry_date }
  }
  return {
    detail,
    service,
    adjustments: billAdjustments(detail.invoice, detail.balancePaisa, credits),
    batches,
  }
}

/** The bill as printed on `format`: the A4 bill or the 80 mm receipt. */
export function billDocument(
  format: PrintFormat,
  data: BillPrintData,
  settings: TenantSettingsRow | null | undefined,
  copyLabel: string | null,
  meta: BillPrintMeta,
): ReactNode {
  const props = {
    invoice: data.detail.invoice,
    lines: data.detail.lines,
    settings,
    balancePaisa: data.detail.balancePaisa,
    batches: data.batches,
    adjustments: data.adjustments,
    copyLabel,
    service: data.service,
    meta,
  }
  return format === 'a4' ? <InvoiceA4 {...props} /> : <Receipt {...props} />
}

/** Who prints a bill and when, the order it came from, and the QR's link. */
export function useBillMeta(): (repo: Repository, invoice: InvoiceRow) => Promise<BillPrintMeta> {
  const { platform } = useSession()
  const printedBy = usePrintedBy()
  return async (repo, invoice) => ({
    ...printedBy(),
    apiUrl: platform.apiUrl,
    reference: invoice.sales_order_id
      ? ((await repo.get('sales_orders', invoice.sales_order_id))?.number ?? null)
      : null,
  })
}

/** Who is printing, and now: for the "Printed by" line of any document. */
export function usePrintedBy(): () => PrintedBy {
  const { state } = useSession()
  const who = state.status === 'signedIn' ? displayName(state.user) : null
  return () => ({ printedBy: who, printedAt: new Date().toISOString() })
}

/** The copy a print or PDF of `invoice` made now will be (`nextPrint`). */
export async function nextCopy(
  repo: Repository,
  invoice: InvoiceRow,
): Promise<{ copyNo: number; label: string | null }> {
  if (invoice.status === 'draft') return { copyNo: 0, label: null }
  const prints = (
    await repo.list('invoice_prints', { where: { invoice_id: invoice.id }, limit: 1000 })
  ).rows
  return nextPrint(invoice, prints)
}

/** Records a print (or a PDF, which is a copy too) of an issued bill as its own tx. */
export async function recordPrint(
  repo: Repository,
  invoice: InvoiceRow,
  format: PrintFormat,
  copyNo: number,
): Promise<void> {
  if (invoice.status === 'draft') return
  await repo.commit([
    invoicePrintOp(
      { tenantId: invoice.tenant_id },
      { invoice_id: invoice.id, format, copy_no: copyNo },
    ),
  ])
}
